import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0Context } from "@/contexts/Auth0Context";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Loader2,
  Cloud,
  CheckCircle,
  AlertCircle,
  LogIn,
  UserPlus,
} from "lucide-react";
import { azureMarketplaceService } from "@/services/azureMarketplace";
import { useToast } from "@/hooks/use-toast";

export function AzureMarketplaceLanding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    isAuthenticated,
    loginWithRedirect,
    isLoading: authLoading,
  } = useAuth0Context();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);

  const token = searchParams.get("token");
  const subscriptionId = searchParams.get("subscriptionId");

  useEffect(() => {
    console.log("AzureMarketplaceLanding useEffect:", {
      token: token ? `present (${token.length} chars)` : "missing",
      tokenPreview: token ? token.substring(0, 50) + "..." : "none",
      subscriptionId: subscriptionId ? "present" : "missing",
      isAuthenticated,
      authLoading,
      fullUrl: window.location.href,
      searchParams: Object.fromEntries(searchParams.entries()),
    });

    // Store the Azure Marketplace parameters in session storage
    if (token || subscriptionId) {
      sessionStorage.setItem(
        "azureMarketplace",
        JSON.stringify({
          token,
          subscriptionId,
          timestamp: Date.now(),
        })
      );
    }

    // If authenticated, proceed to link subscription
    if (isAuthenticated && !authLoading) {
      handleSubscriptionLinking();
    } else if (!authLoading) {
      // Not authenticated, show login options
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, token, subscriptionId]);

  const handleSubscriptionLinking = async () => {
    try {
      setLoading(true);

      // Get stored Azure Marketplace data
      const storedData = sessionStorage.getItem("azureMarketplace");
      console.log(
        "[AzureMarketplaceLanding] Stored marketplace data:",
        storedData
      );
      if (!storedData) {
        setError("No Azure Marketplace subscription data found");
        setLoading(false);
        return;
      }

      const marketplaceData = JSON.parse(storedData);
      console.log(
        "[AzureMarketplaceLanding] Parsed marketplaceData:",
        marketplaceData
      );

      // Call backend to link subscription
      if (marketplaceData.subscriptionId) {
        console.log(
          "[AzureMarketplaceLanding] Linking by subscriptionId:",
          marketplaceData.subscriptionId
        );
        const success = await azureMarketplaceService.linkSubscription(
          marketplaceData.subscriptionId
        );

        if (success) {
          console.log("[AzureMarketplaceLanding] Link result: success");
          // Clear session storage
          sessionStorage.removeItem("azureMarketplace");

          toast({
            title: "Subscription Activated",
            description:
              "Your Azure Marketplace subscription has been successfully linked.",
          });

          // Redirect to success page
          navigate(
            "/azuremarketplace/success?subscriptionId=" +
              marketplaceData.subscriptionId
          );
        } else {
          console.warn("[AzureMarketplaceLanding] Link result: failed");
          setError("Failed to link Azure subscription. Please try again.");
        }
      } else if (marketplaceData.token) {
        // Resolve the token to get subscription information
        console.log(
          "Attempting to resolve token:",
          marketplaceData.token.substring(0, 20) + "..."
        );
        const resolvedSubscription = await azureMarketplaceService.resolveToken(
          marketplaceData.token
        );

        console.log(
          "Token resolution result:",
          resolvedSubscription ? "success" : "failed"
        );
        if (resolvedSubscription) {
          // Store the resolved subscription ID and proceed with linking
          console.log(
            "[AzureMarketplaceLanding] Resolved subscription:",
            resolvedSubscription
          );
          const success = await azureMarketplaceService.linkSubscription(
            resolvedSubscription.id
          );

          if (success) {
            console.log(
              "[AzureMarketplaceLanding] Link result (post-resolve): success"
            );
            // Clear session storage
            sessionStorage.removeItem("azureMarketplace");

            toast({
              title: "Subscription Activated",
              description:
                "Your Azure Marketplace subscription has been successfully linked.",
            });

            // Redirect to success page
            navigate(
              "/azuremarketplace/success?subscriptionId=" +
                resolvedSubscription.id
            );
          } else {
            console.warn(
              "[AzureMarketplaceLanding] Link result (post-resolve): failed"
            );
            setError("Failed to link Azure subscription. Please try again.");
          }
        } else {
          setError(
            "Failed to resolve marketplace token. Please contact support."
          );
        }
      }
    } catch (err) {
      console.error(
        "[AzureMarketplaceLanding] Error linking subscription:",
        err
      );
      setError("An error occurred while linking your subscription.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    // After login, return here so we can complete linking
    const returnTo = window.location.pathname + window.location.search;
    await loginWithRedirect(returnTo);
  };

  const handleSignup = async () => {
    const returnTo = window.location.pathname + window.location.search;
    await loginWithRedirect(returnTo);
  };

  // Loading state while checking auth
  if (authLoading || (loading && isAuthenticated)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
              <p className="text-muted-foreground">
                {isAuthenticated
                  ? "Linking your Azure subscription..."
                  : "Checking authentication..."}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Subscription Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Button onClick={() => navigate("/")} variant="outline">
                Go to Home
              </Button>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not authenticated - show login/signup options
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Cloud className="h-6 w-6 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">
              Azure Marketplace Subscription
            </CardTitle>
            <CardDescription>
              Sign in or create an account to activate your Azure Marketplace
              subscription
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle>Subscription Ready</AlertTitle>
              <AlertDescription>
                Your Azure Marketplace subscription is ready to be activated.
                Please sign in or create a new account to continue.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Button onClick={handleLogin} className="w-full" size="lg">
                <LogIn className="mr-2 h-4 w-4" />
                Sign In to Existing Account
              </Button>

              <Button
                onClick={handleSignup}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Create New Account
              </Button>
            </div>

            <div className="text-center text-sm text-muted-foreground pt-4 border-t">
              <p>
                After signing in, your Azure subscription will be automatically
                linked to your account and you can start using BOLto204.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Authenticated and processing
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-5 w-5" />
            Processing Subscription
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Your subscription is being activated. You will be redirected
            shortly...
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
